import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { dropdownVariants } from '../../utils/animations';
import { SkeletonStatCard, SkeletonListItem, Drawer, MaterialAvatar, useToast } from '../ui';
import EmptyState from '../ui/EmptyState';
import { MARKS } from '../../lib/marks';
import BulkActionBar, { DeleteConfirmModal } from './BulkActionBar';
import LeadDetailModal from './LeadDetailModal';
import KanbanBoard from './KanbanBoard';
import { leadService } from '../../services/leadService';
import { useWebsite } from '../../contexts/WebsiteContext';
import type { Lead } from '../../types';
import { Eyebrow, Field, H1, HairlineGrid, Pill, Rule, Stat, Tag, type TagTone } from '../ui/kit';

// Icons - defined first so they can be used in config objects
const GlobeIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <circle cx="12" cy="12" r="9" strokeLinecap="round" strokeLinejoin="round" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.5 12h17M12 3.5c2 2.5 3.5 5.2 3.5 8.5s-1.5 6-3.5 8.5c-2-2.5-3.5-5.2-3.5-8.5s1.5-6 3.5-8.5z" />
  </svg>
);

const PencilIcon: React.FC = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
  </svg>
);

const UsersIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
  </svg>
);

const ShareIcon: React.FC = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
  </svg>
);

const MessageIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
  </svg>
);

const ZapIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
  </svg>
);

const CalendarIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
  </svg>
);

const MapPinIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
  </svg>
);

const PlusIcon: React.FC = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const DownloadIcon: React.FC = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
  </svg>
);

// CRMLead is now the shared Lead type
type CRMLead = Lead;

interface GroupedLead {
  id: string;           // first (oldest) lead's id
  name: string;         // first lead's name
  email: string | null; // grouping key (null = ungrouped)
  phone: string | null;
  source: string;       // most recent lead's source
  status: string;       // most recent lead's status
  latestLeadId: string; // most recent lead's id (for opening drawer)
  latestLead: CRMLead;  // most recent lead object
  allLeadIds: string[]; // all lead IDs in this group
  unviewedLeadIds: string[]; // leads in this group the owner hasn't opened yet
  hasUnviewed: boolean; // true if any lead in the group is still unread
  messages: Array<{ text: string | null; date: string; leadId: string; name: string; source: string }>;
  messageCount: number; // total number of leads in group
}

const statusConfig: Record<string, { label: string; tone: TagTone }> = {
  new: { label: 'New', tone: 'live' },
  contacted: { label: 'Contacted', tone: 'neutral' },
  qualified: { label: 'Qualified', tone: 'violet' },
  converted: { label: 'Converted', tone: 'live' },
  lost: { label: 'Lost', tone: 'danger' },
};

const sourceConfig: Record<string, { label: string; icon: React.ReactNode }> = {
  website: { label: 'Website', icon: <GlobeIcon /> },
  'Contact Form': { label: 'Contact Form', icon: <MessageIcon /> },
  'Quick Contact': { label: 'Quick Contact', icon: <ZapIcon /> },
  'Booking Request': { label: 'Booking', icon: <CalendarIcon /> },
  'Zip Funnel': { label: 'Zip Funnel', icon: <MapPinIcon /> },
  manual: { label: 'Manual', icon: <PencilIcon /> },
  referral: { label: 'Referral', icon: <UsersIcon /> },
  social: { label: 'Social', icon: <ShareIcon /> },
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatRelativeDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInHours < 1) return 'just now';
  if (diffInHours < 24) return `${diffInHours}h ago`;
  if (diffInDays === 1) return 'yesterday';
  if (diffInDays <= 6) return `${diffInDays} days ago`;
  return formatDate(dateString);
};


const LeadsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { selectedWebsiteId, isAllSelected: isAllWebsites, websites } = useWebsite();

  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterSource, setFilterSource] = useState<string>('');
  const [viewMode, setViewMode] = useState<'table' | 'board'>(() => {
    return (localStorage.getItem('leadsViewMode') as 'table' | 'board') || 'table';
  });
  const [expandedLeadId, setExpandedLeadId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Honest unread count — how many leads across the user's websites have
  // never been opened (CRMLead.viewedAt is null). Replaces the old
  // localStorage `leadsLastSeenAt` heuristic (which didn't survive a device
  // change and lied — see fnd-yb9) and the old markReadByType('lead') mount
  // effect (which wiped the notification bell wholesale on every visit,
  // whether or not any lead was actually opened). Per-lead read state is
  // `lead.viewedAt` (from the `leads` query below); this is just the count,
  // polled at the same 12s cadence the old highlight-reset timer used, and
  // shared (by query key) with the sidebar badge in DashboardLayout.
  useQuery({
    queryKey: ['leads-unread-count'],
    queryFn: () => leadService.getUnreadCount(),
    refetchInterval: 12000,
  });

  /** Mark one or more leads as read. Optimistic, then persisted per lead. */
  const markLeadsViewed = useCallback((leadIds: string[]) => {
    if (leadIds.length === 0) return;
    const nowIso = new Date().toISOString();
    queryClient.setQueriesData(
      { queryKey: ['leads'] },
      (old: { leads: CRMLead[]; total: number } | undefined) => {
        if (!old) return old;
        const idSet = new Set(leadIds);
        return {
          ...old,
          leads: old.leads.map((l) => (idSet.has(l.id) && !l.viewedAt ? { ...l, viewedAt: nowIso } : l)),
        };
      }
    );
    Promise.allSettled(leadIds.map((id) => leadService.markViewed(id)))
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['leads-unread-count'] });
      })
      .catch(() => {});
  }, [queryClient]);

  // Lead detail modal state
  const [selectedLead, setSelectedLead] = useState<CRMLead | null>(null);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);

  // Bulk selection state
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Export leads to CSV
  const handleExportCSV = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const blob = await leadService.exportAll();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast('Leads exported successfully', 'success');
    } catch (error) {
      console.error('Failed to export leads:', error);
      showToast('Failed to export leads', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const selectedWebsite = isAllWebsites ? '' : selectedWebsiteId;
  const currentWebsite = websites.find(w => w.id === selectedWebsite) || websites[0];

  // Fetch leads
  const leadsQueryKey = ['leads', selectedWebsite, filterStatus, filterSource];
  const { data, isLoading, error } = useQuery({
    queryKey: leadsQueryKey,
    queryFn: async () => {
      return leadService.getLeads({
        status: filterStatus || undefined,
        source: filterSource || undefined,
      });
    },
    placeholderData: (previousData) => previousData,
  });

  // Update lead mutation with optimistic updates
  const updateLeadMutation = useMutation({
    mutationFn: async ({ id, ...fields }: { id: string } & Partial<Lead>) => {
      return leadService.updateLead(id, fields);
    },
    onMutate: async ({ id, ...fields }) => {
      await queryClient.cancelQueries({ queryKey: leadsQueryKey });
      const previous = queryClient.getQueryData(leadsQueryKey);
      queryClient.setQueryData(leadsQueryKey, (old: { leads: CRMLead[]; total: number } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          leads: old.leads.map((l) => l.id === id ? { ...l, ...fields } : l),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(leadsQueryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  // Debounced save for lead detail modal edits
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const debouncedLeadSave = useCallback((lead: CRMLead) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      updateLeadMutation.mutate({
        id: lead.id,
        name: lead.name,
        email: lead.email,
        phone: lead.phone,
        source: lead.source,
        status: lead.status,
        notes: lead.notes,
      });
    }, 500);
  }, [updateLeadMutation]);

  const handleStatusChange = (leadId: string, newStatus: string) => {
    updateLeadMutation.mutate({ id: leadId, status: newStatus });
  };

  const leads = data?.leads || [];

  // Group leads by email address
  const groupedLeads = useMemo<GroupedLead[]>(() => {
    if (leads.length === 0) return [];

    const emailGroups = new Map<string, CRMLead[]>();
    const ungrouped: CRMLead[] = [];

    // Sort all leads by date ascending (oldest first) for consistent ordering
    const sorted = [...leads].sort((a, b) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    for (const lead of sorted) {
      const email = lead.email?.trim().toLowerCase();
      if (email) {
        if (!emailGroups.has(email)) {
          emailGroups.set(email, []);
        }
        emailGroups.get(email)!.push(lead);
      } else {
        ungrouped.push(lead);
      }
    }

    const result: GroupedLead[] = [];

    for (const [email, groupLeads] of emailGroups) {
      const first = groupLeads[0]; // oldest
      const latest = groupLeads[groupLeads.length - 1]; // most recent

      // Messages: up to 3 most recent, newest first
      const recentMessages = [...groupLeads]
        .reverse()
        .slice(0, 3)
        .map((l) => ({
          text: l.notes || null,
          date: l.createdAt,
          leadId: l.id,
          name: l.name,
          source: l.source,
        }));

      const unviewed = groupLeads.filter((l) => !l.viewedAt);
      result.push({
        id: first.id,
        name: first.name,
        email: email,
        phone: latest.phone || first.phone || null,
        source: latest.source,
        status: latest.status,
        latestLeadId: latest.id,
        latestLead: latest,
        allLeadIds: groupLeads.map((l) => l.id),
        unviewedLeadIds: unviewed.map((l) => l.id),
        hasUnviewed: unviewed.length > 0,
        messages: recentMessages,
        messageCount: groupLeads.length,
      });
    }

    // Ungrouped leads (no email)
    for (const lead of ungrouped) {
      result.push({
        id: lead.id,
        name: lead.name,
        email: null,
        phone: lead.phone || null,
        source: lead.source,
        status: lead.status,
        latestLeadId: lead.id,
        latestLead: lead,
        allLeadIds: [lead.id],
        unviewedLeadIds: lead.viewedAt ? [] : [lead.id],
        hasUnviewed: !lead.viewedAt,
        messages: [{ text: lead.notes || null, date: lead.createdAt, leadId: lead.id, name: lead.name, source: lead.source }],
        messageCount: 1,
      });
    }

    // Sort groups by most recent lead date (newest first)
    result.sort((a, b) => {
      const aDate = new Date(a.messages[0]?.date || 0).getTime();
      const bDate = new Date(b.messages[0]?.date || 0).getTime();
      return bDate - aDate;
    });

    return result;
  }, [leads]);

  // Bulk selection helpers - work with grouped leads
  const allLeadIds = useMemo(() => leads.map((l: CRMLead) => l.id), [leads]);
  const allGroupIds = useMemo(() => groupedLeads.map((g) => g.id), [groupedLeads]);
  const isAllSelected = allGroupIds.length > 0 && allGroupIds.every((id: string) => selectedLeadIds.has(id));
  const isSomeSelected = selectedLeadIds.size > 0 && !isAllSelected;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedLeadIds(new Set());
    } else {
      setSelectedLeadIds(new Set(allGroupIds));
    }
  };

  const handleSelectLead = (leadId: string) => {
    const newSelected = new Set(selectedLeadIds);
    if (newSelected.has(leadId)) {
      newSelected.delete(leadId);
    } else {
      newSelected.add(leadId);
    }
    setSelectedLeadIds(newSelected);
  };

  const clearSelection = () => {
    setSelectedLeadIds(new Set());
  };

  // Clear selection when leads change (e.g., after filter/delete)
  useEffect(() => {
    setSelectedLeadIds(new Set());
  }, [filterStatus, filterSource, selectedWebsite]);

  // Resolve selected group IDs to all underlying lead IDs
  const resolveSelectedLeadIds = useCallback((): string[] => {
    const resolved: string[] = [];
    for (const groupId of selectedLeadIds) {
      const group = groupedLeads.find((g) => g.id === groupId);
      if (group) {
        resolved.push(...group.allLeadIds);
      } else {
        resolved.push(groupId);
      }
    }
    return resolved;
  }, [selectedLeadIds, groupedLeads]);

  // Bulk status update with optimistic updates
  const handleBulkStatusChange = async (status: string) => {
    if (selectedLeadIds.size === 0) return;
    setIsBulkProcessing(true);
    const resolvedIds = resolveSelectedLeadIds();
    const resolvedSet = new Set(resolvedIds);
    // Optimistic update
    const previous = queryClient.getQueryData(leadsQueryKey);
    queryClient.setQueryData(leadsQueryKey, (old: { leads: CRMLead[]; total: number } | undefined) => {
      if (!old) return old;
      return {
        ...old,
        leads: old.leads.map((l) => resolvedSet.has(l.id) ? { ...l, status } : l),
      };
    });
    try {
      await leadService.bulkUpdateStatus(resolvedIds, status);
      showToast(`Updated ${selectedLeadIds.size} leads to ${status}`, 'success');
      clearSelection();
    } catch (error) {
      console.error('Bulk status update failed:', error);
      queryClient.setQueryData(leadsQueryKey, previous);
      showToast('Failed to update leads', 'error');
    } finally {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setIsBulkProcessing(false);
    }
  };

  // Bulk delete with optimistic updates
  const handleBulkDelete = async () => {
    if (selectedLeadIds.size === 0) return;
    setIsBulkProcessing(true);
    const resolvedIds = resolveSelectedLeadIds();
    const resolvedSet = new Set(resolvedIds);
    const previous = queryClient.getQueryData(leadsQueryKey);
    queryClient.setQueryData(leadsQueryKey, (old: { leads: CRMLead[]; total: number } | undefined) => {
      if (!old) return old;
      const remaining = old.leads.filter((l) => !resolvedSet.has(l.id));
      return { ...old, leads: remaining, total: remaining.length };
    });
    try {
      await leadService.bulkDelete(resolvedIds);
      showToast(`Deleted ${selectedLeadIds.size} leads`, 'success');
      clearSelection();
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Bulk delete failed:', error);
      queryClient.setQueryData(leadsQueryKey, previous);
      showToast('Failed to delete leads', 'error');
    } finally {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setIsBulkProcessing(false);
    }
  };

  // Bulk export
  const handleBulkExport = async () => {
    if (selectedLeadIds.size === 0) return;
    setIsBulkProcessing(true);
    const resolvedIds = resolveSelectedLeadIds();
    const resolvedSet = new Set(resolvedIds);
    try {
      const blob = await leadService.bulkExport(resolvedIds);
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads-export-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      showToast(`Exported ${selectedLeadIds.size} leads`, 'success');
    } catch (error) {
      console.error('Export failed:', error);
      showToast('Failed to export leads', 'error');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const stats = {
    total: leads.length,
    new: leads.filter((l: CRMLead) => l.status === 'new').length,
    contacted: leads.filter((l: CRMLead) => l.status === 'contacted').length,
    qualified: leads.filter((l: CRMLead) => l.status === 'qualified').length,
    converted: leads.filter((l: CRMLead) => l.status === 'converted').length,
    lost: leads.filter((l: CRMLead) => l.status === 'lost').length,
  };

  const isLoadingData = isLoading && !data;

  if (error) {
    return (
      <div className="bg-red-50 border border-red-100 rounded-xl p-6">
        <h3 className="font-semibold text-red-900">Error loading leads</h3>
        <p className="text-sm text-red-700 mt-1">Unable to load your leads. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header - Always visible */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <Eyebrow className="mb-3">Customers</Eyebrow>
          <H1>Leads</H1>
          <Rule className="mt-5" />
        </div>
        {!isLoadingData && leads.length > 0 && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* View Mode Toggle */}
            <div className="flex bg-paper-deep ring-1 ring-zinc-900/[0.04] rounded-lg p-0.5">
              <button
                onClick={() => { setViewMode('table'); localStorage.setItem('leadsViewMode', 'table'); }}
                className={`p-2 rounded-md transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm' : 'text-muted hover:text-primary'}`}
                title="Table view"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                </svg>
              </button>
              <button
                onClick={() => { setViewMode('board'); localStorage.setItem('leadsViewMode', 'board'); }}
                className={`p-2 rounded-md transition-colors ${viewMode === 'board' ? 'bg-white shadow-sm' : 'text-muted hover:text-primary'}`}
                title="Board view"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 4.5v15m6-15v15m-10.875 0h15.75c.621 0 1.125-.504 1.125-1.125V5.625c0-.621-.504-1.125-1.125-1.125H4.125C3.504 4.5 3 5.004 3 5.625v12.75c0 .621.504 1.125 1.125 1.125Z" />
                </svg>
              </button>
            </div>
            <motion.button
              onClick={handleExportCSV}
              disabled={isExporting}
              whileHover={{ scale: isExporting ? 1 : 1.02 }}
              whileTap={{ scale: isExporting ? 1 : 0.98 }}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-full bg-white/60 text-sm font-medium text-primary ring-1 ring-zinc-300 transition-all hover:bg-white hover:ring-zinc-400 disabled:opacity-50"
              title="Export all leads to CSV"
            >
              <DownloadIcon />
              <span className="hidden sm:inline">{isExporting ? 'Exporting...' : 'Export CSV'}</span>
            </motion.button>
            <motion.button
              onClick={() => setShowAddModal(true)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-full bg-white/60 text-sm font-medium text-primary ring-1 ring-zinc-300 transition-all hover:bg-white hover:ring-zinc-400"
              title="Add new lead"
            >
              <PlusIcon />
              <span className="hidden sm:inline">Add Lead</span>
            </motion.button>
          </div>
        )}
      </div>

      {/* Stats Overview — hairline cells, no icon tiles */}
      {isLoadingData ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <SkeletonStatCard key={i} />
          ))}
        </div>
      ) : (
        <HairlineGrid cols="grid-cols-2 lg:grid-cols-4">
          <Stat label="Total leads" value={stats.total} />
          <Stat label="New" value={stats.new} />
          <Stat label="Converted" value={stats.converted} />
          <Stat label="In progress" value={stats.contacted + stats.qualified} />
        </HairlineGrid>
      )}

      {/* Filters - Google Style */}
      <div className="flex flex-wrap gap-2">
        <FilterDropdown
          value={filterStatus}
          onChange={setFilterStatus}
          options={[
            { value: '', label: 'All Statuses' },
            { value: 'new', label: 'New' },
            { value: 'contacted', label: 'Contacted' },
            { value: 'qualified', label: 'Qualified' },
            { value: 'converted', label: 'Converted' },
            { value: 'lost', label: 'Lost' },
          ]}
        />
        <FilterDropdown
          value={filterSource}
          onChange={setFilterSource}
          options={[
            { value: '', label: 'All Sources' },
            { value: 'website', label: 'Website' },
            { value: 'Contact Form', label: 'Contact Form' },
            { value: 'Quick Contact', label: 'Quick Contact' },
            { value: 'Booking Request', label: 'Booking' },
            { value: 'Zip Funnel', label: 'Zip Funnel' },
            { value: 'manual', label: 'Manual' },
            { value: 'referral', label: 'Referral' },
            { value: 'social', label: 'Social' },
          ]}
        />
        <AnimatePresence>
          {(filterStatus || filterSource) && (
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={() => {
                setFilterStatus('');
                setFilterSource('');
              }}
              className="px-3 py-2 text-sm text-muted hover:text-primary transition-colors"
            >
              Clear filters
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Leads View */}
      <div>
        {isLoadingData ? (
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="divide-y divide-zinc-100">
              {[...Array(5)].map((_, i) => (
                <SkeletonListItem key={i} />
              ))}
            </div>
          </div>
        ) : leads.length === 0 ? (
          <EmptyState
            mark={MARKS.leads}
            eyebrow="Pipeline"
            title="No leads yet"
            description="Everyone who calls or fills in a form on your site shows up here, newest first."
            hint="Captured from your contact form, callback form and tracking number."
            action={{ label: "Add Lead", onClick: () => setShowAddModal(true) }}
          />
        ) : viewMode === 'board' ? (
          <KanbanBoard
            leads={leads}
            onStatusChange={handleStatusChange}
            onLeadClick={(lead) => {
              setSelectedLead(lead);
              setIsLeadModalOpen(true);
              if (!lead.viewedAt) markLeadsViewed([lead.id]);
            }}
          />
        ) : (
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            {/* Select All Header */}
            <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50">
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    className="sr-only"
                  />
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      isAllSelected
                        ? 'bg-accent border-accent'
                        : isSomeSelected
                        ? 'bg-accent/50 border-accent'
                        : 'border-zinc-300 hover:border-zinc-400'
                    }`}
                  >
                    {isAllSelected && (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    )}
                    {isSomeSelected && !isAllSelected && (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-sm font-medium text-muted">
                  {selectedLeadIds.size > 0
                    ? `${selectedLeadIds.size} of ${groupedLeads.length} selected`
                    : 'Select all'}
                </span>
              </label>
            </div>
            <div className="divide-y divide-zinc-100">
              {groupedLeads.map((group, index) => (
                <GroupedLeadRow
                  key={group.id}
                  group={group}
                  index={index}
                  isExpanded={expandedLeadId === group.id}
                  onToggle={() => setExpandedLeadId(expandedLeadId === group.id ? null : group.id)}
                  onStatusChange={(status) => {
                    // Update all leads in the group
                    for (const leadId of group.allLeadIds) {
                      handleStatusChange(leadId, status);
                    }
                  }}
                  isSelected={selectedLeadIds.has(group.id)}
                  onSelect={() => handleSelectLead(group.id)}
                  isUnseen={group.hasUnviewed}
                  onViewDetails={() => {
                    setSelectedLead(group.latestLead);
                    setIsLeadModalOpen(true);
                    markLeadsViewed(group.unviewedLeadIds);
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add Lead Drawer */}
      {currentWebsite && (
        <AddLeadDrawer
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          websiteId={currentWebsite.id}
          websiteName={currentWebsite.businessName || currentWebsite.domain}
        />
      )}

      {/* Bulk Action Bar */}
      <BulkActionBar
        selectedCount={selectedLeadIds.size}
        onStatusChange={handleBulkStatusChange}
        onExport={handleBulkExport}
        onDelete={() => setShowDeleteConfirm(true)}
        onClearSelection={clearSelection}
        isProcessing={isBulkProcessing}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        count={selectedLeadIds.size}
        onConfirm={handleBulkDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        isDeleting={isBulkProcessing}
      />

      {/* Lead Detail Modal */}
      <LeadDetailModal
        lead={selectedLead}
        isOpen={isLeadModalOpen}
        onClose={() => {
          setIsLeadModalOpen(false);
          setSelectedLead(null);
        }}
        onSave={(updatedLead) => {
          setSelectedLead(updatedLead as CRMLead);
          debouncedLeadSave(updatedLead as CRMLead);
        }}
      />
    </div>
  );
};

// Grouped Lead Row Component
interface GroupedLeadRowProps {
  group: GroupedLead;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onStatusChange: (status: string) => void;
  isSelected?: boolean;
  onSelect?: () => void;
  onViewDetails?: () => void;
  isUnseen?: boolean;
}

const GroupedLeadRow: React.FC<GroupedLeadRowProps> = ({ group, index, isExpanded, onToggle, onStatusChange, isSelected = false, onSelect, onViewDetails, isUnseen = false }) => {
  const status = statusConfig[group.status] || statusConfig.new;
  const source = sourceConfig[group.source] || sourceConfig.manual;

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect?.();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.2 }}
      className={`cursor-pointer transition-colors ${
        isSelected
          ? 'bg-accent/5 hover:bg-accent/10'
          : isUnseen
            ? 'hover:!bg-green-100/60'
            : 'hover:bg-zinc-100/80'
      }`}
      style={isUnseen ? { animation: 'leadHighlight 12s ease-out forwards' } : undefined}
    >
      <div
        className="p-4"
        onClick={() => onViewDetails?.()}
      >
        <div className="flex items-center gap-4">
          {/* Checkbox */}
          {onSelect && (
            <div
              className="relative flex-shrink-0"
              onClick={handleCheckboxClick}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => {}}
                className="sr-only"
              />
              <div
                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                  isSelected
                    ? 'bg-accent border-accent'
                    : 'border-zinc-300 hover:border-zinc-400'
                }`}
              >
                {isSelected && (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                )}
              </div>
            </div>
          )}

          {/* Avatar with message count badge */}
          <div className="relative flex-shrink-0">
            <MaterialAvatar seed={group.id} name={group.name} />
            {group.messageCount > 1 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {group.messageCount}
              </span>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-primary truncate">{group.name}</h3>
              <Tag tone={status.tone}>
                {status.label}
              </Tag>
            </div>
            <div className="flex items-center gap-3 mt-1 text-sm text-muted">
              {group.email && <span className="truncate">{group.email}</span>}
              {group.phone && <span>{group.phone}</span>}
            </div>
            {/* Mini message list for grouped leads */}
            {group.messageCount > 1 && (
              <div className="mt-2 space-y-1">
                {group.messages.map((msg, i) => (
                  <div key={msg.leadId} className="flex items-start gap-2 text-xs text-muted">
                    <span className="text-zinc-400 flex-shrink-0 mt-0.5">
                      {i === 0 ? (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="4" />
                        </svg>
                      ) : (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <circle cx="12" cy="12" r="4" />
                        </svg>
                      )}
                    </span>
                    <span className="truncate flex-1">
                      {msg.text || (
                        <span className="italic text-zinc-400">
                          {(sourceConfig[msg.source]?.label || msg.source)} submission
                        </span>
                      )}
                    </span>
                    <span className="flex-shrink-0 text-zinc-400">{formatRelativeDate(msg.date)}</span>
                  </div>
                ))}
                {group.messageCount > 3 && (
                  <div className="text-xs text-zinc-400 pl-5">
                    +{group.messageCount - 3} more message{group.messageCount - 3 > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Meta */}
          <div className="hidden sm:flex items-center gap-4 text-sm text-muted flex-shrink-0">
            <div className="flex items-center gap-1">
              {source.icon}
              <span>{source.label}</span>
            </div>
            <span>{formatRelativeDate(group.messages[0]?.date || '')}</span>
          </div>

          {/* Expand Icon */}
          <svg
            className="w-5 h-5 text-zinc-400 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </div>
      </div>

      {/* Expanded Details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-0 bg-zinc-50 border-t border-zinc-100">
              <div className="grid md:grid-cols-2 gap-6 pt-4">
                {/* Latest Notes */}
                <div>
                  <h4 className="text-sm font-medium text-primary mb-2">Latest Notes</h4>
                  <p className="text-sm text-muted">{group.latestLead.notes || 'No notes added.'}</p>
                </div>

                {/* Timeline */}
                <div>
                  <h4 className="text-sm font-medium text-primary mb-2">Timeline</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted">
                      <span className="w-2 h-2 bg-blue-500 rounded-full" />
                      <span>First contact {formatDate(group.messages[group.messages.length - 1]?.date || '')}</span>
                    </div>
                    {group.messageCount > 1 && (
                      <div className="flex items-center gap-2 text-muted">
                        <span className="w-2 h-2 bg-purple-500 rounded-full" />
                        <span>{group.messageCount} total submissions</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-muted">
                      <span className="w-2 h-2 bg-green-500 rounded-full" />
                      <span>Latest {formatDate(group.messages[0]?.date || '')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-zinc-200">
                {onViewDetails && (
                  <motion.button
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewDetails();
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-3 py-1 bg-primary hover:bg-ink-soft text-white rounded-lg text-xs font-medium transition-colors"
                  >
                    View Details
                  </motion.button>
                )}
                <span className="text-sm font-medium text-muted mr-2">Change status:</span>
                {Object.entries(statusConfig).map(([key, config]) => (
                  <motion.button
                    key={key}
                    onClick={(e) => {
                      e.stopPropagation();
                      onStatusChange(key);
                    }}
                    disabled={group.status === key}
                    whileHover={{ scale: group.status === key ? 1 : 1.05 }}
                    whileTap={{ scale: group.status === key ? 1 : 0.95 }}
                    className={`rounded-full transition-opacity ${
                      group.status === key ? 'cursor-not-allowed opacity-45' : 'hover:opacity-75'
                    }`}
                  >
                    <Tag tone={config.tone}>{config.label}</Tag>
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Add Lead Drawer Component
interface AddLeadDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  websiteId: string;
  websiteName: string;
}

const AddLeadDrawer: React.FC<AddLeadDrawerProps> = ({ isOpen, onClose, websiteId, websiteName }) => {
  const queryClient = useQueryClient();
  // The submit button lives in the drawer's pinned footer, outside the form.
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    source: 'manual',
    notes: '',
  });

  const createLeadMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return leadService.createLead({ ...data, websiteId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setFormData({ name: '', email: '', phone: '', source: 'manual', notes: '' });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    createLeadMutation.mutate(formData);
  };

  const canSubmit = formData.name.trim().length > 0;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      eyebrow="New lead"
      title="Add someone by hand"
      subtitle={`Goes into ${websiteName}, alongside the ones your website captures.`}
      footer={
        <div className="flex gap-2">
          <Pill tone="outline" className="flex-1" onClick={onClose}>
            Cancel
          </Pill>
          <Pill
            className="flex-1"
            onClick={() => formRef.current?.requestSubmit()}
            disabled={!canSubmit || createLeadMutation.isPending}
          >
            {createLeadMutation.isPending ? 'Adding…' : 'Add lead'}
          </Pill>
        </div>
      }
    >
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-8">
        <section className="space-y-5">
          <Eyebrow>Contact</Eyebrow>
          <Field label="Name" htmlFor="new-lead-name" hint="The only thing that's required.">
            <input
              id="new-lead-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input-field"
              placeholder="Contact name"
              required
            />
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Phone" htmlFor="new-lead-phone">
              <input
                id="new-lead-phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="input-field"
                placeholder="(555) 123-4567"
              />
            </Field>
            <Field label="Email" htmlFor="new-lead-email">
              <input
                id="new-lead-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-field"
                placeholder="email@example.com"
              />
            </Field>
          </div>
        </section>

        <section className="space-y-2">
          <span className="block font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
            Where they came from
          </span>
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'manual', label: 'Walk-in' },
              { value: 'website', label: 'Website' },
              { value: 'referral', label: 'Referral' },
              { value: 'social', label: 'Social' },
            ].map((source) => {
              const on = formData.source === source.value;
              return (
                <button
                  key={source.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, source: source.value })}
                  aria-pressed={on}
                  className={`rounded-full px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-eyebrow transition-colors ${
                    on
                      ? 'bg-primary text-white'
                      : 'bg-white text-zinc-500 ring-1 ring-zinc-300 hover:ring-zinc-400'
                  }`}
                >
                  {source.label}
                </button>
              );
            })}
          </div>
        </section>

        <section>
          <Field label="Notes" htmlFor="new-lead-notes" hint="What they asked for, when to call back.">
            <textarea
              id="new-lead-notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="input-field leading-relaxed"
              rows={4}
              placeholder="Wants a quote for a bathroom re-pipe, free after 5pm…"
            />
          </Field>
        </section>
      </form>
    </Drawer>
  );
};

// Filter Dropdown Component - Google Style
interface FilterDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find(opt => opt.value === value) || options[0];
  const isFiltered = value !== '';

  return (
    <div className="relative">
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full border transition-all
          ${isFiltered
            ? 'bg-accent/10 border-accent/30 text-accent'
            : 'bg-white border-zinc-200 text-primary hover:bg-zinc-100'
          }
        `}
      >
        {selectedOption?.label}
        <motion.svg
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </motion.svg>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              variants={dropdownVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="absolute top-full left-0 mt-1 bg-white border border-zinc-200 rounded-xl shadow-lg z-20 min-w-[160px] overflow-hidden"
            >
              {options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors
                    ${option.value === value
                      ? 'bg-accent/10 text-accent font-medium'
                      : 'text-primary hover:bg-zinc-100'
                    }
                  `}
                >
                  {option.label}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LeadsPage;
